import type { MapLoadCause } from '@voll-intelligence/domain';

export interface MapInstance {
  fitBounds(bounds: unknown, padding?: number): void;
  addListener(event: string, listener: () => void): { remove(): void };
}

export interface MarkerInstance {
  addListener(event: string, listener: () => void): void;
  setMap(map: null): void;
  setZIndex(zIndex: number): void;
}

export interface MapsApi {
  Map: new (element: HTMLElement, options: Record<string, unknown>) => MapInstance;
  Marker: new (options: Record<string, unknown>) => MarkerInstance;
  LatLngBounds: new () => { extend(point: { lat: number; lng: number }): void };
  Polyline: new (options: {
    path: readonly { lat: number; lng: number }[];
    strokeColor: string;
    strokeOpacity: number;
    strokeWeight: number;
    map: MapInstance;
    icons?: readonly unknown[];
  }) => { setMap(map: null): void };
}

declare global {
  interface Window {
    google?: { maps?: MapsApi };
  }
}

/** Por que o SDK não veio — o vocabulário é o do evento `map_load_failed`
 *  (`MapLoadCauseSchema`, no domain). `script_error` é rede ou bloqueio,
 *  `timeout` é lentidão além de `SDK_TIMEOUT_MS`, `no_api` é o callback sem
 *  `google.maps`, `init_error` é o SDK carregado que estourou ao instanciar o mapa.
 *  (`no_tiles`, o mapa que nasce e não pinta, vem de `watchFirstPaint`.) */
class MapsLoadError extends Error {
  constructor(readonly kind: MapLoadCause) {
    super(`Google Maps unavailable: ${kind}`);
  }
}

export function loadCause(error: unknown): MapLoadCause {
  return error instanceof MapsLoadError ? error.kind : 'init_error';
}

const SDK_TIMEOUT_MS = 15_000;
/** Prazo do primeiro `tilesloaded` de um mapa À VISTA. Um mapa são pinta em fração
 *  de segundo (~0,4 s medido no Chromium); o do "Mapa ampliado" em dev (30/09)
 *  seguia cinza, só com os rótulos dos pinos, depois de ~8 s (#4034) — e, sem
 *  prazo, contava como pronto para sempre: mudo para a pessoa e para a telemetria. */
const TILES_TIMEOUT_MS = 10_000;

/** Vigia a primeira pintura: `onMissing` se o mapa ficar `TILES_TIMEOUT_MS` À VISTA
 *  (no viewport, com a aba visível) sem `tilesloaded`. Fora da tela o prazo não
 *  corre e sair dela o zera: o SDK só inicializa e pinta o mapa que entra no
 *  viewport (medido no Chromium: mapa abaixo da dobra não pede tile nem emite
 *  `idle`/`tilesloaded` até a rolagem chegar nele) — contar ali seria alarme falso
 *  para todo card antigo da conversa. Devolve o desligamento. */
export function watchFirstPaint(frame: HTMLElement, map: MapInstance, onMissing: () => void) {
  let onScreen = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  const arm = () => {
    clearTimeout(timer);
    if (!onScreen || document.visibilityState === 'hidden') return;
    timer = setTimeout(() => {
      stop();
      onMissing();
    }, TILES_TIMEOUT_MS);
  };
  const observer = new IntersectionObserver((entries) => {
    onScreen = entries.at(-1)?.isIntersecting ?? onScreen;
    arm();
  });
  const painted = map.addListener('tilesloaded', () => {
    stop();
  });
  function stop() {
    clearTimeout(timer);
    observer.disconnect();
    document.removeEventListener('visibilitychange', arm);
    painted.remove();
  }
  observer.observe(frame);
  document.addEventListener('visibilitychange', arm);
  return stop;
}

/** Só enquanto o SDK está A CAMINHO, e só para a MESMA chave: quem chega no meio
 *  espera a mesma carga; outra chave começa a sua. Depois, `window.google.maps`
 *  é a fonte — nada fica em cache aqui. */
let pending: { apiKey: string; promise: Promise<MapsApi> } | undefined;
/** Nome de callback único por carga — um contador, não `Date.now()`: duas cargas
 *  no mesmo milissegundo (ou sob fake timers) partilhariam o nome. */
let loadCount = 0;

export function loadMaps(apiKey: string): Promise<MapsApi> {
  if (window.google?.maps) return Promise.resolve(window.google.maps);
  if (pending?.apiKey === apiKey) return pending.promise;
  const promise = new Promise<MapsApi>((resolve, reject) => {
    loadCount += 1;
    const callback = `__vollGoogleMaps_${String(loadCount)}`;
    const win = window as unknown as Record<string, unknown>;
    // Encerra ESTA carga nos dois desfechos: um `error` tardio no script já
    // carregado não pode mais mexer no estado de uma carga seguinte.
    const settle = () => {
      clearTimeout(timeout);
      script.onerror = null;
      win[callback] = undefined;
      if (pending?.promise === promise) pending = undefined;
    };
    const fail = (kind: MapLoadCause) => {
      settle();
      script.remove();
      reject(new MapsLoadError(kind));
    };
    win[callback] = () => {
      if (window.google?.maps) {
        settle();
        resolve(window.google.maps);
      } else {
        fail('no_api');
      }
    };
    const script = document.createElement('script');
    const query = new URLSearchParams({ key: apiKey, loading: 'async', callback, v: 'weekly' });
    script.src = `https://maps.googleapis.com/maps/api/js?${query.toString()}`;
    script.async = true;
    script.onerror = () => {
      fail('script_error');
    };
    document.head.append(script);
    const timeout = setTimeout(() => {
      fail('timeout');
    }, SDK_TIMEOUT_MS);
  });
  pending = { apiKey, promise };
  return promise;
}
